import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from '@if/core/yup'
import { ControlledSelect, ControlledTextField } from '@if/mui'
import { Button, Grid, InputAdornment, MenuItem, Snackbar, Typography } from '@mui/material'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import { AsyncActionButton } from '@src/components'
import { useCreatePaymentPlan } from '@src/data/api/merchants-api/merchants-api'
import { CreatePaymentPlanDto } from '@src/data/types/CreatePaymentPlanDto'
import { useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

const VALID_LOAN_TERMS = [6, 12, 18, 24, 36, 48, 60, 72, 84]

interface Props {
  open: boolean
  handleClose: () => void
  activePromoCodes: string[]
}

const CreatePromoCodeDialog = ({ open, handleClose, activePromoCodes }: Props) => {
  const { t } = useTranslation()

  const CreatePromoCodeSchema = useMemo(() => {
    return yup.object().shape({
      promoCode: yup
        .string()
        .uppercase()
        .required()
        .test('promoCode-already-in-use', t('browsePromotions.errors.promoCodeAlreadyInUse'), function (value) {
          return !activePromoCodes.map((s) => s.toUpperCase()).includes(value.toUpperCase())
        }),
      interestRate: yup.number().min(0).max(100).required(),
      reducedInterestRate: yup.number().min(0).max(100).required(),
      loanTerm: yup.integer().oneOf(VALID_LOAN_TERMS).required(),
      reducedRateDurationInMonths: yup
        .integer()
        .positive()
        .required()
        .test(
          'reduced-rate-duration-not-more-than-loan-term',
          t('browsePromotions.errors.reducedRateDurationShouldBeAtMostLoanTerm'),
          function (value) {
            const { loanTerm } = this.parent as { loanTerm: number }
            return value <= loanTerm
          },
        ),
    })
  }, [t, activePromoCodes])

  type CreatePromoCodeSchemaType = yup.InferType<typeof CreatePromoCodeSchema>

  const [createPaymentPlan, isCreating, isCreatingError, creatingError] = useCreatePaymentPlan()

  const { control, reset, handleSubmit } = useForm<CreatePromoCodeSchemaType>({
    mode: 'onBlur',
    defaultValues: { reducedInterestRate: 0 },
    resolver: yupResolver(CreatePromoCodeSchema),
  })

  const [openSnackbar, setOpenSnackbar] = useState(false)
  const [snackbarPromoCode, setSnackbarPromoCode] = useState('')

  const closeDialog = () => {
    handleClose()
    reset()
  }

  const showSnackbar = (promoCode: string) => {
    setSnackbarPromoCode(promoCode)
    setOpenSnackbar(true)
  }

  const onSubmit = async (formData: CreatePaymentPlanDto) => {
    await createPaymentPlan(formData).then((paymentPlanDto) => {
      showSnackbar(paymentPlanDto.promoCode)
      closeDialog()
    })
  }

  return (
    <>
      <Dialog open={open} onClose={closeDialog} fullWidth maxWidth="md">
        <DialogTitle>{t('browsePromotions.createPromo')}</DialogTitle>
        <DialogContent>
          <Grid container columns={12} spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <ControlledTextField name="promoCode" control={control} label={t('merchant.promo.promoCode')} fullWidth />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}></Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <ControlledTextField
                name="interestRate"
                control={control}
                label={t('merchant.promo.interestRate')}
                fullWidth
                slotProps={{
                  input: {
                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                  },
                }}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <ControlledTextField
                name="reducedInterestRate"
                control={control}
                label={t('merchant.promo.reducedInterestRate')}
                fullWidth
                slotProps={{
                  input: {
                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                  },
                }}
                disabled
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <ControlledSelect name="loanTerm" control={control} label={t('merchant.promo.loanTerm')} fullWidth>
                {VALID_LOAN_TERMS.map((term) => (
                  <MenuItem value={term} key={term}>{`${term} ${t('common.months')}`}</MenuItem>
                ))}
              </ControlledSelect>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <ControlledTextField
                name="reducedRateDurationInMonths"
                control={control}
                label={t('merchant.promo.reducedRateDuration')}
                fullWidth
                slotProps={{
                  input: {
                    endAdornment: <InputAdornment position="end">{t('common.months')}</InputAdornment>,
                  },
                }}
              />
            </Grid>
          </Grid>

          {isCreatingError && (
            <Typography color="error" sx={{ mt: 2 }}>
              {t('common.error')}: {creatingError?.message} : {String(creatingError?.response?.data)}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <AsyncActionButton
            variant="contained"
            color="primary"
            isPending={isCreating}
            onClick={handleSubmit(onSubmit)}
          >
            {t('common.confirm')}
          </AsyncActionButton>
          <Button variant="contained" onClick={closeDialog}>
            {t('common.cancel')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={5000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        message={t('browsePromotions.promotionCreatedWithCode', { promoCode: snackbarPromoCode })}
      />
    </>
  )
}

export default CreatePromoCodeDialog
